/**
 * ADR-118 — active-organization + masquerade hooks.
 */

import { useContext } from 'react';
import type { BaseUser } from '@aegix/auth-core';
import { useUser } from '@aegix/auth-identity';
import { OrganizationContext } from './provider';
import type { Organization, WithVendorFlag } from './types';

function useOrganizationContext() {
  const ctx = useContext(OrganizationContext);
  if (!ctx) {
    throw new Error(
      '[auth-organization] hooks called outside <OrganizationProvider>. ' +
        'Mount <OrganizationProvider> inside <AuthProvider> at the app root.',
    );
  }
  return ctx;
}

/**
 * Returns the currently active organization, or `null` if none is
 * set. The lib does NOT auto-select an org — apps choose how to
 * default (e.g. "first school in user.schools", "from URL param").
 */
export function useActiveOrganization(): Organization | null {
  return useOrganizationContext().activeOrganization;
}

/**
 * Returns a setter for the active organization. Pass `null` to clear.
 *
 * Setting persists to localStorage (so a reload restores it) AND
 * fires the cross-tab heartbeat (so other tabs pick up the change
 * within one event-loop tick via `<OrganizationProvider>`'s
 * subscription).
 */
export function useSetActiveOrganization(): (
  org: Organization | null,
) => void {
  return useOrganizationContext().setActiveOrganization;
}

/**
 * Returns `true` if the current user can use the vendor-masquerade
 * UI (SMS `VendorOrgSelector` + AIM equivalent). The flag is
 * computed server-side in ``UserSerializer.get_is_vendor_user`` and
 * shipped on the user payload — true when:
 *   * the user has the per-vendor FK (`vendor_id` set), OR
 *   * the user has the Aegix-internal
 *     ``accounts.can_access_all_onboarding_orgs`` permission.
 *
 * The lib reads `user.is_vendor_user`. Apps mounting this hook MUST
 * inject a user type that extends `WithVendorFlag`. Anonymous users
 * return `false` (cannot masquerade).
 */
export function useIsVendorMasqueradeAvailable(): boolean {
  const user = useUser<BaseUser & WithVendorFlag>();
  if (!user) return false;
  return Boolean((user as Partial<WithVendorFlag>).is_vendor_user);
}
